(() => {
  const money = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.Galactica?.moneyFormat);
    }
    const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return `${value} €`;
  };

  const parseGrams = (label) => {
    if (!label) return null;
    const match = String(label)
      .toLowerCase()
      .replace(',', '.')
      .match(/(\d+(?:\.\d+)?)\s*g\b/);
    if (match) return Number(match[1]);
    const compact = String(label).toLowerCase().match(/(\d+(?:\.\d+)?)g(?:ramme?s?)?$/);
    return compact ? Number(compact[1]) : null;
  };

  const minPricePerGram = (product, { fromPrefix = true } = {}) => {
    let best = null;
    let weightCount = 0;
    for (const v of product.variants || []) {
      const label = v.public_title || v.title || v.option1 || '';
      const g = parseGrams(label);
      if (!g || g <= 0) continue;
      if (!(Number(v.price) > 0)) continue;
      weightCount += 1;
      const ppg = v.price / g;
      if (best === null || ppg < best.ppg) best = { ppg, price: v.price, g };
    }
    if (!best) return null;
    const formatted = `${money(Math.round(best.ppg))}/g`;
    if (fromPrefix && weightCount > 1) return `À partir de ${formatted}`;
    return formatted;
  };

  const extractCbdLabel = (product) => {
    const fromTags = (product.tags || []).find((t) => /cbd/i.test(t) && /%/.test(t));
    if (fromTags) return fromTags;
    const titleMatch = String(product.title || '').match(/\d+(?:[.,]\d+)?\s*%\s*cbd/i);
    if (titleMatch) return titleMatch[0];
    return null;
  };

  const FINDER_CONTEXT_KEY = 'gal-finder-context';

  const readFinderContext = () => {
    try {
      const raw = sessionStorage.getItem(FINDER_CONTEXT_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (!data.intent && !data.profile && !data.family) return null;
      return data;
    } catch {
      return null;
    }
  };

  const writeFinderContext = (answers) => {
    try {
      if (!answers || typeof answers !== 'object') return;
      const payload = {
        intent: answers.intent || '',
        profile: answers.profile || '',
        family: answers.family || '',
        updatedAt: Date.now(),
      };
      sessionStorage.setItem(FINDER_CONTEXT_KEY, JSON.stringify(payload));
      document.dispatchEvent(new CustomEvent('gal:finder:context', { detail: payload }));
    } catch {
      /* private mode */
    }
  };

  const clearFinderContext = () => {
    try {
      sessionStorage.removeItem(FINDER_CONTEXT_KEY);
      document.dispatchEvent(new CustomEvent('gal:finder:context', { detail: null }));
    } catch {
      /* private mode */
    }
  };

  const parseCbdValue = (value) => {
    if (value == null || value === '') return null;
    const n = Number(String(value).replace('%', '').replace(',', '.').trim());
    return Number.isFinite(n) ? n : null;
  };

  const scoreFinderProduct = (product, answers, { excludeId } = {}) => {
    if (!product) return { score: 0, tier: 99 };
    const id = String(product.id || product.dataset?.id || '');
    if (excludeId && id && String(excludeId) === id) return { score: -999, tier: 99 };

    const available =
      product.available === true ||
      product.available === 'true' ||
      product.dataset?.available === 'true';
    if (product.available === false || product.dataset?.available === 'false') {
      return { score: -50, tier: 99 };
    }

    const intents = String(product.intents || product.dataset?.intents || '').toLowerCase();
    const intensity = String(product.intensity || product.dataset?.intensity || '').toLowerCase();
    const sellingType = String(product.sellingType || product.dataset?.sellingType || product.dataset?.['selling-type'] || '').toLowerCase();
    const culture = String(product.culture || product.dataset?.culture || product.growing || '').toLowerCase();
    const type = String(product.type || product.dataset?.type || '').toLowerCase();
    const tags = String(product.tags || product.dataset?.tags || '').toLowerCase();
    const collections = String(
      Array.isArray(product.collections) ? product.collections.join(',') : product.collections || product.dataset?.collections || ''
    ).toLowerCase();
    const title = String(product.title || product.dataset?.title || '').toLowerCase();
    const blob = `${title} ${type} ${sellingType} ${culture} ${intents} ${intensity} ${tags} ${collections}`;
    const cbd = parseCbdValue(product.cbd || product.dataset?.cbd);

    const intent = String(answers?.intent || '').toLowerCase();
    const profile = String(answers?.profile || '').toLowerCase();
    const family = String(answers?.family || '').toLowerCase();

    let intentHit = false;
    let intensityHit = false;
    let formatHit = false;
    let familyHit = false;
    let score = 0;

    if (intent === 'detente') {
      if (/detente|détente/.test(intents) || /detente|détente|relax/.test(blob)) {
        intentHit = true;
        score += 40;
      }
    } else if (intent === 'soir') {
      if (/soir_sommeil|soir|sommeil/.test(intents) || /soir|sommeil|night/.test(blob)) {
        intentHit = true;
        score += 40;
      } else if (/detente|détente/.test(intents)) {
        score += 12;
      }
    } else if (intent === 'polyvalent') {
      if (/polyvalent/.test(intents) || /polyvalent|découvrir|decouvrir/.test(blob)) {
        intentHit = true;
        score += 40;
      }
    }

    if (profile === 'doux') {
      if (/doux|soft|léger|leger/.test(intensity) || (cbd != null && cbd < 18)) {
        intensityHit = true;
        score += 28;
      } else if (cbd != null && cbd < 25) score += 8;
    } else if (profile === 'equilibre') {
      if (/équilibr|equilibr|medium|moyen/.test(intensity) || (cbd != null && cbd >= 18 && cbd <= 28)) {
        intensityHit = true;
        score += 28;
      } else if (cbd != null) score += 8;
    } else if (profile === 'intense') {
      if (/fort|intense|strong|puissant|ultra/.test(intensity) || (cbd != null && cbd >= 25)) {
        intensityHit = true;
        score += 28;
      } else if (cbd != null && cbd >= 20) score += 8;
    }

    const familyMap = {
      fleurs: ['fleur', 'fleurs', 'bud', 'indoor', 'greenhouse', 'herbe', 'fleurs-indoor', 'fleurs-greenhouse'],
      resines: ['résine', 'resine', 'pollen', 'hash', 'charas', 'resines', 'resines-et-pollen', 'resines-et-pollens'],
      rocks: ['rock', 'rocks', 'moonrock', 'goldrock', 'icerock', 'concentré', 'concentre', 'les-rocks'],
    };
    if (family && family !== 'any') {
      const keys = familyMap[family] || [];
      formatHit = keys.some((k) => blob.includes(k));
      if (formatHit) {
        score += 32;
        familyHit = true;
      } else {
        score -= 40;
      }
      // Keep accessories out of flower / resin / rock journeys
      if (/accessoir|feuille|papier|grinder|briquet|chicha|tips?/.test(blob)) {
        score -= 60;
      }
    }

    if (sellingType && familyHit) score += 6;
    if (culture) score += 2;
    if (available) score += 10;
    if (cbd != null) score += 2;

    let tier = 5;
    if (intentHit && intensityHit && formatHit) tier = 1;
    else if (intentHit && formatHit) tier = 2;
    else if (intentHit) tier = 3;
    else if (familyHit || formatHit) tier = 4;

    return { score, tier, intentHit, intensityHit, formatHit, familyHit };
  };

  const readWishlist = () => {
    try {
      const raw = JSON.parse(localStorage.getItem('gal-wishlist') || '[]');
      return raw.filter((x) => typeof x === 'string' && x.length > 0);
    } catch {
      return [];
    }
  };

  const syncWishlistPressed = () => {
    const list = readWishlist();
    document.querySelectorAll('[data-gal-wishlist]').forEach((btn) => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      btn.setAttribute('aria-pressed', list.includes(handle) ? 'true' : 'false');
    });
  };

  class GalProductCard extends HTMLElement {
    connectedCallback() {
      this.bindVariantUi();
    }

    bindVariantUi() {
      this.select = this.querySelector('[data-gal-variant-select]');
      this.pads = [...this.querySelectorAll('[data-gal-variant-pad]')];
      this.variantInput = this.querySelector('[data-gal-variant-id]');
      this.addBtn = this.querySelector('[data-gal-add]');
      this.priceRoot = this.querySelector('[data-gal-price]');
      this.valueLabel = this.querySelector('[data-gal-card-value-label]');
      this.form = this.querySelector('form');

      /* Host-level delegation survives innerHTML swaps / section re-renders */
      if (this.dataset.galCardBound !== '1') {
        this.dataset.galCardBound = '1';
        this.addEventListener('change', (event) => {
          const select = event.target?.closest?.('[data-gal-variant-select]');
          if (!select || !this.contains(select)) return;
          this.select = select;
          const option = select.selectedOptions[0];
          this.syncPads(option?.value);
          this.applyVariant(option, { userSelected: true });
        });
        this.addEventListener('click', (event) => {
          const pad = event.target?.closest?.('[data-gal-variant-pad]');
          if (!pad || !this.contains(pad) || pad.disabled) return;
          this.pads = [...this.querySelectorAll('[data-gal-variant-pad]')];
          this.select = this.querySelector('[data-gal-variant-select]');
          this.syncPads(pad.dataset.variantId);
          if (this.select) this.select.value = pad.dataset.variantId;
          this.applyVariant(pad, { userSelected: true });
        });
        this.addEventListener('submit', async (event) => {
          if (event.target !== this.querySelector('form')) return;
          if (!window.Galactica || window.Galactica.settings.cartType !== 'drawer') return;
          event.preventDefault();
          this.variantInput = this.querySelector('[data-gal-variant-id]');
          const id = this.variantInput?.value;
          if (!id) return;
          try {
            await fetch(window.Galactica.routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
            });
            document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
            document.dispatchEvent(new CustomEvent('gal:cart:open'));
          } catch (error) {
            event.target.submit();
          }
        });
      }

      if (this.select) {
        const selected = this.select.selectedOptions[0];
        const selectedUnavailable =
          selected &&
          (selected.disabled || String(selected.dataset.available) === 'false');
        if (selectedUnavailable) {
          const available = [...this.select.options].find(
            (o) => !o.disabled && String(o.dataset.available) !== 'false',
          );
          if (available) this.select.value = available.value;
        }
      }

      const initial =
        this.select?.selectedOptions?.[0] ||
        this.pads.find((p) => p.classList.contains('is-active')) ||
        this.pads[0];
      if (initial) this.applyVariant(initial, { userSelected: false });
    }

    syncPads(variantId) {
      const id = String(variantId || '');
      this.pads.forEach((p) => {
        const active = String(p.dataset.variantId) === id;
        p.classList.toggle('is-active', active);
        p.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    applyVariant(source, { userSelected = false } = {}) {
      if (!source) return;
      const id = source.dataset.variantId || source.value;
      const price = Number(source.dataset.price || 0);
      const compare = Number(source.dataset.compare || 0);
      const available = String(source.dataset.available) !== 'false' && source.dataset.available !== 'false';
      const label = source.dataset.label || source.textContent || '';
      const valueLabel = source.dataset.valueLabel || '';
      const grams =
        Number(source.dataset.grams) ||
        parseGrams(label) ||
        Number(this.priceRoot?.dataset.grams) ||
        null;
      if (this.priceRoot && grams) this.priceRoot.dataset.grams = String(grams);

      this.syncPads(id);
      if (this.select && id) this.select.value = id;
      if (this.variantInput) this.variantInput.value = id;
      if (this.addBtn) {
        this.addBtn.disabled = !available;
        this.addBtn.textContent = available
          ? window.Galactica?.strings?.addToCart || this.addBtn.textContent || 'Ajouter'
          : window.Galactica?.strings?.soldOut || 'Épuisé';
      }
      if (this.valueLabel) {
        if (valueLabel) {
          this.valueLabel.hidden = false;
          this.valueLabel.textContent = valueLabel;
        } else {
          this.valueLabel.hidden = true;
          this.valueLabel.textContent = '';
        }
      }
      if (this.priceRoot) {
        const current = this.priceRoot.querySelector('[data-gal-price-current]');
        const compareEl = this.priceRoot.querySelector('[data-gal-price-compare]');
        let perGramEl = this.priceRoot.querySelector('[data-gal-price-per-gram]');
        if (current) current.textContent = money(price);
        if (compare > price) {
          if (!compareEl) {
            const span = document.createElement('span');
            span.className = 'gal-price__compare';
            span.dataset.galPriceCompare = '';
            current?.after(span);
          }
          const el = this.priceRoot.querySelector('[data-gal-price-compare]');
          if (el) el.textContent = money(compare);
        } else if (compareEl) {
          compareEl.remove();
        }
        if (grams && grams > 0 && perGramEl) {
          perGramEl.textContent = `${money(Math.round(price / grams))}/g`;
        }
      }

      const fromEl = this.querySelector('[data-gal-card-per-gram]');
      if (fromEl && grams && grams > 0) {
        const multi = fromEl.dataset.galMultiWeight === '1';
        const selectedPpg = money(Math.round(price / grams));
        if (userSelected || !multi) {
          fromEl.textContent = `${selectedPpg}/g`;
          fromEl.dataset.galSelected = '1';
        } else if (!fromEl.dataset.galSelected) {
          const fromLabel = fromEl.dataset.galFromLabel || 'À partir de';
          const bestCents = Number(fromEl.dataset.galBestPerGram || 0);
          fromEl.textContent = bestCents
            ? `${fromLabel} ${money(bestCents)}/g`
            : `${fromLabel} ${selectedPpg}/g`;
        }
      }

      this.dispatchEvent(
        new CustomEvent('gal:variant-change', { bubbles: true, detail: { id, price, available, grams } })
      );
    }
  }

  class GalCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-gal-cart-panel]');
      this.closeButtons = this.querySelectorAll('[data-gal-cart-close]');
      this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.querySelector('[data-gal-cart-overlay]')?.addEventListener('click', () => this.close());
      document.addEventListener('gal:cart:open', () => this.open());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.querySelector('[data-gal-cart-close]')?.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  class GalPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-gal-search-results]');
      if (!this.input || !this.results) return;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.query(this.input.value.trim()), 220);
      });
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim()) this.results.hidden = false;
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.results.hidden = true;
      });
    }

    async query(terms) {
      if (!terms) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        return;
      }
      const url = `${window.Galactica.routes.predictive_search_url}?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      try {
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const content = doc.querySelector('#predictive-search-results') || doc.body;
        this.results.innerHTML = content.innerHTML;
        this.results.hidden = false;
      } catch (e) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      }
    }
  }

  class GalQuiz extends HTMLElement {
    connectedCallback() {
      this.step = 0;
      this.answers = {};
      this.loadingTimer = null;
      this.steps = [...this.querySelectorAll('[data-gal-quiz-step]')];
      try {
        this.catalog = JSON.parse(this.querySelector('[data-gal-quiz-catalog]')?.textContent || '[]');
      } catch {
        this.catalog = [];
      }
      this.render();
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-gal-quiz-restart]')) {
          this.reset();
          return;
        }
        const back = e.target.closest('[data-gal-quiz-back]');
        const answer = e.target.closest('[data-gal-quiz-answer]');
        if (answer) {
          this.answers[answer.dataset.key] = answer.dataset.value;
          this.step = Math.min(this.step + 1, this.steps.length - 1);
          this.render();
          if (this.steps[this.step]?.hasAttribute('data-gal-quiz-loading-step')) {
            this.runAnalysis();
          }
        }
        if (back) {
          clearTimeout(this.loadingTimer);
          this.step = Math.max(this.step - 1, 0);
          this.render();
        }
      });
    }

    disconnectedCallback() {
      clearTimeout(this.loadingTimer);
    }

    reset() {
      clearTimeout(this.loadingTimer);
      this.step = 0;
      this.answers = {};
      clearFinderContext();
      this.render();
    }

    runAnalysis() {
      clearTimeout(this.loadingTimer);
      const needleHost = this.querySelector('[data-gal-finder-loader]') || this.querySelector('[data-gal-finder-needle]');
      const play = window.Galactica?.playLoadingGauge || window.Galactica?.playNeedleMeter;
      const finish = () => {
        this.step = Math.min(this.step + 1, this.steps.length - 1);
        this.render();
        if (this.steps[this.step]?.hasAttribute('data-gal-quiz-results-step')) this.showResults();
      };
      if (typeof play === 'function' && needleHost) {
        play(needleHost, { duration: 2200 }).then(finish);
      } else {
        this.loadingTimer = setTimeout(finish, 1800);
      }
    }

    parseCbd(value) {
      if (value == null || value === '') return null;
      const n = Number(String(value).replace('%', '').replace(',', '.').trim());
      return Number.isFinite(n) ? n : null;
    }

    bestVariant(product) {
      const variants = (product.variants || []).filter((v) => v && v.available !== false);
      const pool = variants.length ? variants : product.variants || [];
      if (!pool.length) return null;
      const format = (this.answers.format || '').toLowerCase();
      const withGrams = pool
        .map((v) => ({
          ...v,
          grams: v.grams || parseGrams(v.title) || null,
          ppg: v.grams || parseGrams(v.title) ? v.price / (v.grams || parseGrams(v.title)) : null,
        }))
        .filter((v) => v.grams);

      if (format === 'best_pg' && withGrams.length) {
        return withGrams.sort((a, b) => a.ppg - b.ppg)[0];
      }
      if (format === 'petit' && withGrams.length) {
        return withGrams.sort((a, b) => a.grams - b.grams)[0];
      }
      if (format === 'grand' && withGrams.length) {
        return withGrams.sort((a, b) => b.grams - a.grams)[0];
      }
      if (format === 'standard' && withGrams.length) {
        const mid = withGrams.find((v) => v.grams >= 5 && v.grams <= 15) || withGrams.sort((a, b) => a.grams - b.grams)[Math.floor(withGrams.length / 2)];
        return mid;
      }
      return pool.find((v) => v.available) || pool[0];
    }

    familyLabel(key) {
      return (
        {
          fleurs: 'Fleur',
          resines: 'Résine',
          rocks: 'Rock / concentré',
          any: 'Produit',
        }[key] || 'Produit'
      );
    }

    scoreProduct(product) {
      let score = 0;
      const facts = [];
      const intents = String(product.intents || '').toLowerCase();
      const blob = `${product.title} ${product.type} ${product.sellingType || ''} ${product.growing || ''} ${intents} ${(product.tags || []).join(' ')} ${(product.collections || []).join(' ')}`.toLowerCase();

      const intent = (this.answers.intent || '').toLowerCase();
      if (intent === 'detente') {
        if (/detente|détente/.test(intents) || /detente|détente|relax/.test(blob)) {
          score += 7;
          facts.push('Profil recherché : Détente');
        } else score += 2;
      } else if (intent === 'soir') {
        if (/soir_sommeil|soir|sommeil/.test(intents) || /soir|sommeil|night/.test(blob)) {
          score += 7;
          facts.push('Moment : Soir');
        } else if (/detente|détente/.test(intents)) {
          score += 3;
          facts.push('Sélection relaxation');
        } else score += 1;
      } else if (intent === 'polyvalent') {
        if (/polyvalent/.test(intents)) {
          score += 6;
          facts.push('Sélection polyvalente');
        } else score += 3;
      }

      const family = (this.answers.family || '').toLowerCase();
      const familyMap = {
        fleurs: ['fleur', 'fleurs', 'bud', 'indoor', 'greenhouse', 'herbe', 'fleurs-indoor', 'fleurs-greenhouse', 'culture'],
        resines: ['résine', 'resine', 'pollen', 'hash', 'charas', 'resines', 'resines-et-pollen', 'resines-et-pollens'],
        rocks: ['rock', 'rocks', 'moonrock', 'goldrock', 'icerock', 'concentré', 'concentre', 'les-rocks'],
        huiles: ['huile', 'oil', 'huile-cbd'],
        accessoires: ['accessoir', 'accesoir', 'chicha', 'feuille', 'blunt'],
      };

      if (family && family !== 'any') {
        const keys = familyMap[family] || [];
        const hit =
          keys.some((k) => blob.includes(k)) ||
          (product.collections || []).some((h) => keys.some((k) => String(h).includes(k)));
        if (hit) {
          score += 8;
          facts.push(this.familyLabel(family));
        } else {
          score -= 2;
        }
      } else {
        score += 1;
      }

      const profile = (this.answers.profile || '').toLowerCase();
      const cbd = this.parseCbd(product.cbd);
      const intensity = String(product.intensity || '').toLowerCase();
      if (profile === 'doux') {
        if ((cbd != null && cbd < 18) || /doux|soft|léger|leger/.test(intensity + blob)) {
          score += 5;
          facts.push('Intensité douce');
        } else if (cbd != null && cbd < 25) score += 2;
      } else if (profile === 'equilibre') {
        if ((cbd != null && cbd >= 18 && cbd <= 28) || /équilibr|medium|moyen/.test(intensity + blob)) {
          score += 5;
          facts.push('Intensité équilibrée');
        } else if (cbd != null) score += 2;
      } else if (profile === 'intense') {
        if ((cbd != null && cbd >= 25) || /fort|intense|strong|puissant|ultra/.test(intensity + blob)) {
          score += 5;
          facts.push('Intensité marquée');
        } else if (cbd != null && cbd >= 20) score += 2;
      }

      const variant = this.bestVariant(product);
      if (variant?.grams) {
        score += 1;
        facts.push(`format ${variant.title} disponible`);
      }

      if (product.available || (variant && variant.available)) score += 3;
      else score -= 6;

      if (product.growing) facts.push(String(product.growing));
      if (cbd != null) score += 1;

      const reason = facts.filter(Boolean).slice(0, 3).join(' · ') || 'Disponible · correspond à vos choix';
      return { score, reason, variant, cbd };
    }

    showResults() {
      const root = this.querySelector('[data-gal-quiz-results]');
      if (!root) return;
      writeFinderContext(this.answers);
      const ranked = this.catalog
        .map((p) => ({ ...p, ...this.scoreProduct(p) }))
        .filter((p) => p.score > 0 && (p.available || p.variant?.available))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);

      if (!ranked.length) {
        root.innerHTML =
          '<p class="gal-finder__empty">Aucune correspondance nette pour l’instant. Parcours le catalogue ou recommence.</p>';
        return;
      }

      root.innerHTML = ranked
        .map((p, index) => {
          const v = p.variant || {};
          const price = money(v.price || 0);
          const grams = v.grams || parseGrams(v.title);
          const perGram = grams ? `${money(Math.round(v.price / grams))}/g` : '';
          const cbd = p.cbd != null ? `${p.cbd}% CBD` : '';
          const second =
            p.cbc && String(p.cbc).trim()
              ? `${String(p.cbc).replace('%', '')}% CBC`
              : p.cbg && String(p.cbg).trim()
                ? `${String(p.cbg).replace('%', '')}% CBG`
                : '';
          const stars =
            p.rating && Number(p.rating) > 0
              ? `<p class="gal-finder__result-reviews"><span aria-hidden="true">${'★'.repeat(Math.round(Number(p.rating)))}${'☆'.repeat(5 - Math.round(Number(p.rating)))}</span>${p.ratingCount ? ` ${p.ratingCount} avis` : ''}</p>`
              : '';
          return `<article class="gal-finder__result${index === 0 ? ' is-primary' : ''}">
            ${index === 0 ? '<p class="gal-finder__badge">Meilleur match</p>' : ''}
            <div class="gal-finder__result-grid">
              ${p.image ? `<a class="gal-finder__result-media" href="${p.url}"><img src="${p.image}" alt="" width="240" height="240" loading="lazy"></a>` : '<div class="gal-finder__result-media"></div>'}
              <div class="gal-finder__result-body">
                <h4><a href="${p.url}">${p.title}</a></h4>
                ${stars}
                <p class="gal-finder__result-meta">${[v.title, perGram, cbd, second].filter(Boolean).join(' · ')}</p>
                <p class="gal-finder__result-price">${price}</p>
                <p class="gal-finder__result-why">${p.reason}</p>
                <div class="gal-finder__result-actions">
                  <a class="gal-btn gal-btn--primary" href="${p.url}">Voir le produit</a>
                </div>
              </div>
            </div>
          </article>`;
        })
        .join('');
    }

    render() {
      this.steps.forEach((step, index) => {
        step.hidden = index !== this.step;
      });
      const dialog = this.closest('[data-gal-finder]');
      const meters = dialog?.querySelectorAll('[data-gal-finder-step-meter]');
      const questionSteps = this.steps.filter(
        (s) => !s.hasAttribute('data-gal-quiz-results-step') && !s.hasAttribute('data-gal-quiz-loading-step')
      ).length;
      if (meters?.length) {
        const activeIdx = Math.min(this.step, questionSteps - 1);
        const inQuestions = this.step < questionSteps;
        meters.forEach((meter, i) => {
          const on = inQuestions && i <= activeIdx;
          meter.classList.toggle('is-active', on);
          meter.style.setProperty('--gal-load-gauge', on ? '100%' : '0%');
          meter.style.setProperty('--gal-vu', on ? '100%' : '0%');
        });
      }
      const label = dialog?.querySelector('[data-gal-finder-step-label]');
      if (label) {
        if (this.steps[this.step]?.hasAttribute('data-gal-quiz-loading-step')) label.textContent = 'Analyse en cours';
        else if (this.steps[this.step]?.hasAttribute('data-gal-quiz-results-step')) label.textContent = 'Résultats';
        else label.textContent = `Étape ${Math.min(this.step + 1, questionSteps)} sur ${questionSteps}`;
      }
      const firstFocus = this.steps[this.step]?.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      if (firstFocus && dialog?.open) {
        requestAnimationFrame(() => firstFocus.focus({ preventScroll: true }));
      }
    }
  }

  class GalOrderTracker extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-gal-tracker-form]') || this.querySelector('form');
      if (!this.form || this._bound) return;
      this._bound = true;
      this.statusEl = this.querySelector('[data-gal-tracker-status]');
      this.loadingEl = this.querySelector('[data-gal-tracker-loading]');
      this.successEl = this.querySelector('[data-gal-tracker-success]');
      this.submitBtn = this.querySelector('[data-gal-tracker-submit]');
      this.openLink = this.querySelector('[data-gal-tracker-open]');
      this.gauge = this.loadingEl?.querySelector('[data-gal-loading-gauge]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    resetUi() {
      this.setStatus('');
      this.successEl?.setAttribute('hidden', '');
      this.loadingEl?.setAttribute('hidden', '');
      this.gauge?.classList.remove('is-active', 'is-complete');
      this.gauge?.style.setProperty('--gal-load-gauge', '0%');
      if (this.submitBtn) this.submitBtn.hidden = false;
      if (this.openLink) {
        this.openLink.hidden = true;
        this.openLink.removeAttribute('href');
      }
      this.form?.querySelectorAll('input').forEach((el) => {
        el.disabled = false;
      });
    }

    setStatus(msg, { error = false } = {}) {
      if (!this.statusEl) return;
      if (!msg) {
        this.statusEl.textContent = '';
        this.statusEl.hidden = true;
        this.statusEl.classList.remove('is-error');
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = msg;
      this.statusEl.classList.toggle('is-error', !!error);
    }

    setLoading(on) {
      if (!this.loadingEl) return;
      if (on) {
        this.loadingEl.removeAttribute('hidden');
        this.gauge?.classList.add('is-active');
        this.gauge?.classList.remove('is-complete');
        this.gauge?.style.setProperty('--gal-load-gauge', '72%');
        if (this.submitBtn) this.submitBtn.disabled = true;
      } else {
        this.loadingEl.setAttribute('hidden', '');
        this.gauge?.classList.remove('is-active');
        if (this.submitBtn) this.submitBtn.disabled = false;
      }
    }

    showSuccess(data) {
      const title =
        this.dataset.labelSuccess || 'Expédition retrouvée';
      const successTitle = this.querySelector('[data-gal-tracker-success-title]');
      const carrier = this.querySelector('[data-gal-tracker-carrier]');
      const number = this.querySelector('[data-gal-tracker-number]');
      if (successTitle) successTitle.textContent = title;
      if (carrier) carrier.textContent = data.tracking_company || '—';
      if (number) number.textContent = data.tracking_number || '—';
      this.successEl?.removeAttribute('hidden');
      if (this.submitBtn) this.submitBtn.hidden = true;
      this.form?.querySelectorAll('input').forEach((el) => {
        el.disabled = true;
      });
      const url = String(data.tracking_url || '').trim();
      if (this.openLink && url && /^https?:\/\//i.test(url)) {
        this.openLink.href = url;
        this.openLink.hidden = false;
      } else if (this.openLink) {
        this.openLink.hidden = true;
      }
      this.gauge?.classList.add('is-complete');
      this.gauge?.style.setProperty('--gal-load-gauge', '100%');
    }

    async onSubmit(e) {
      e.preventDefault();
      const tracking =
        this.form.querySelector('[name="tracking_number"]')?.value?.trim() ||
        this.form.querySelector('[name="order"]')?.value?.trim() ||
        '';
      const email = this.form.querySelector('[name="email"]')?.value?.trim() || '';
      const requiredMsg =
        this.dataset.msgRequired || 'Numéro de suivi et e-mail requis.';
      const notFoundMsg =
        this.dataset.msgNotFound ||
        "Nous n’avons pas trouvé d’expédition correspondant à ces informations.";
      const pendingMsg =
        this.dataset.msgPending ||
        'Online tracking is not available yet. Check your confirmation email or contact support.';

      this.setStatus('');
      this.successEl?.setAttribute('hidden', '');
      if (this.submitBtn) this.submitBtn.hidden = false;

      if (!tracking || !email) {
        this.setStatus(requiredMsg, { error: true });
        return;
      }

      const endpoint = (this.dataset.trackingEndpoint || '').trim();
      if (!endpoint) {
        this.setStatus(pendingMsg, { error: true });
        return;
      }

      this.setLoading(true);
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ tracking_number: tracking, email }),
        });
        const data = await res.json().catch(() => null);
        this.setLoading(false);
        if (res.status === 429) {
          this.setStatus(notFoundMsg, { error: true });
          return;
        }
        if (!res.ok || !data || data.found !== true) {
          this.setStatus(notFoundMsg, { error: true });
          return;
        }
        this.setStatus('');
        this.showSuccess(data);
      } catch {
        this.setLoading(false);
        this.setStatus(notFoundMsg, { error: true });
      }
    }
  }

  const COMPARE_KEY = 'gal-compare';
  const COMPARE_MAX = 3;
  const COMPARE_LABEL = 'Comparer';
  const COMPARE_ADDED = 'Ajouté au comparateur';
  const COMPARE_LIMIT_MSG = 'Vous pouvez comparer jusqu’à 3 produits.';

  const readCompareList = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(COMPARE_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => (typeof item === 'string' ? { handle: item } : item))
        .filter((item) => item && typeof item.handle === 'string' && item.handle.length)
        .slice(0, COMPARE_MAX);
    } catch {
      return [];
    }
  };

  const writeCompareList = (list, { silent = false } = {}) => {
    localStorage.setItem(COMPARE_KEY, JSON.stringify(list.slice(0, COMPARE_MAX)));
    if (!silent) {
      document.dispatchEvent(new CustomEvent('gal:compare:update', { detail: { list: readCompareList() } }));
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const moleculeFromProduct = (product, key) => {
    const re = new RegExp(`${key}\\s*[:=]?\\s*(\\d+(?:[.,]\\d+)?)\\s*%`, 'i');
    for (const tag of product.tags || []) {
      const m = String(tag).match(re);
      if (m) return `${m[1].replace(',', '.')}%`;
    }
    const title = String(product.title || '');
    const m2 = title.match(re);
    if (m2) return `${m2[1].replace(',', '.')}%`;
    return null;
  };

  const vuCell = (label, pct) => {
    if (!pct) return '—';
    const match = String(pct).match(/(\d+(?:[.,]\d+)?)/);
    if (!match) return '—';
    const num = Math.min(100, Math.max(0, parseFloat(match[1].replace(',', '.')) || 0));
    if (num <= 0) return '—';
    return `<div class="gal-gauge gal-gauge--inline" style="--gal-gauge:${num}%" data-gal-gauge="${label.toLowerCase()}" data-gal-gauge-pct="${num}"><div class="gal-gauge__head"><span class="gal-gauge__label">${label}</span><span class="gal-gauge__value" data-gal-gauge-value>${num}%</span></div><div class="gal-gauge__track" aria-hidden="true"><span class="gal-gauge__fill" style="width:${num}%"></span></div></div>`;
  };

  const metaFromButton = (btn) => ({
    handle: btn.dataset.productHandle,
    title: btn.dataset.productTitle || btn.dataset.productHandle || '',
    image: btn.dataset.productImage || '',
    cbd: btn.dataset.productCbd || '',
    cbg: btn.dataset.productCbg || '',
    cbc: btn.dataset.productCbc || '',
    type: btn.dataset.productType || '',
    culture: btn.dataset.productCulture || '',
    sellingType: btn.dataset.productSellingType || '',
    intensity: btn.dataset.productIntensity || '',
    flavor: btn.dataset.productFlavor || '',
    usageIntents: btn.dataset.productUsageIntents || '',
    origin: btn.dataset.productOrigin || '',
    texture: btn.dataset.productTexture || '',
    rating: btn.dataset.productRating || '',
    ratingCount: btn.dataset.productRatingCount || '',
  });

  const persistCompareItem = (item, product) => ({
    handle: product?.handle || item.handle,
    title: product?.title || item.title || item.handle,
    image: product?.featured_image || item.image || '',
    type: product?.type || item.type || '',
    cbd: item.cbd || '',
    cbg: item.cbg || '',
    cbc: item.cbc || '',
    culture: item.culture || '',
    sellingType: item.sellingType || '',
    intensity: item.intensity || '',
    flavor: item.flavor || '',
    usageIntents: item.usageIntents || '',
    origin: item.origin || '',
    texture: item.texture || '',
    rating: item.rating || '',
    ratingCount: item.ratingCount || '',
  });

  const formatUsageIntents = (raw) => {
    if (!raw) return '';
    return String(raw)
      .split(/[,|]/)
      .map((part) =>
        part
          .trim()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
      )
      .filter(Boolean)
      .join(', ');
  };

  const formatFamily = (product, meta) => {
    const type = product?.type || meta?.type || '';
    const selling = meta?.sellingType || '';
    if (type && selling && type.toLowerCase() !== selling.toLowerCase()) return `${type} · ${selling}`;
    return type || selling || '';
  };

  const pickVariant = (product, selectedId) => {
    const variants = product?.variants || [];
    if (selectedId) {
      const match = variants.find((v) => String(v.id) === String(selectedId));
      if (match) return match;
    }
    return variants.find((v) => v.available) || variants[0] || null;
  };

  const variantPriceHtml = (variant, product) => {
    const price = variant?.price ?? product?.price ?? 0;
    const compare = Number(variant?.compare_at_price || 0);
    if (compare > price) {
      return `<span class="gal-price" data-gal-compare-price>${money(price)}</span> <s class="gal-price__compare">${money(compare)}</s>`;
    }
    return `<span class="gal-price" data-gal-compare-price>${money(price)}</span>`;
  };

  const variantPerGram = (variant) => {
    if (!variant) return '';
    const label = variant.public_title || variant.title || variant.option1 || '';
    const g = parseGrams(label);
    if (!g || !(Number(variant.price) > 0)) return '';
    return `${money(Math.round(variant.price / g))}/g`;
  };

  const variantAvailability = (variant, product) => {
    if (variant) return variant.available ? 'En stock' : 'Épuisé';
    return product?.available ? 'En stock' : 'Épuisé';
  };

  const cbdNumber = (meta, product) => {
    const raw = meta?.cbd || moleculeFromProduct(product, 'cbd') || extractCbdLabel(product) || '';
    return parseCbdValue(raw);
  };

  const ratingHtml = (meta) => {
    const rating = Number(String(meta?.rating || '').replace(',', '.'));
    if (!Number.isFinite(rating) || rating <= 0) return '';
    const rounded = Math.max(0, Math.min(5, Math.round(rating)));
    const stars = `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
    const count = meta?.ratingCount ? ` <span class="gal-muted">(${escapeHtml(meta.ratingCount)})</span>` : '';
    return `<span class="gal-compare-rating" aria-label="${escapeHtml(String(rating))} / 5"><span aria-hidden="true">${stars}</span>${count}</span>`;
  };

  const cellHasValue = (v) => {
    if (v == null) return false;
    const text = String(v)
      .replace(/<[^>]*>/g, '')
      .replace(/—/g, '')
      .trim();
    return text.length > 0;
  };

  class GalCompare extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galCompareBound === '1') return;
      this.dataset.galCompareBound = '1';

      this.dock = document.querySelector('[data-gal-compare-dock]');
      this.modal = document.querySelector('[data-gal-compare-modal]');
      this.board = this.modal?.querySelector('[data-gal-compare-board]') || document.querySelector('[data-gal-compare-board]');
      this.status = this.modal?.querySelector('[data-gal-compare-status]');
      this.loading = this.modal?.querySelector('[data-gal-compare-loading]');
      this.thumbs = document.querySelector('[data-gal-compare-thumbs]');
      this.limitToast = document.querySelector('[data-gal-compare-limit]');
      this._boardEntries = [];
      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this.isModalOpen()) this.closeModal();
      };

      document.addEventListener('click', (e) => this.onClick(e));
      document.addEventListener('change', (e) => this.onChange(e));
      document.addEventListener('gal:compare:update', () => this.syncUi());
      document.addEventListener('keydown', this._onKeydown);
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target?.hasAttribute?.('data-gal-compare-modal')) this.closeModal();
      });
      this.modal?.addEventListener('cancel', () => {
        document.body.classList.remove('gal-compare-open');
      });
      this.modal?.addEventListener('close', () => {
        document.body.classList.remove('gal-compare-open');
      });
      this.syncUi();
    }

    isModalOpen() {
      if (!this.modal) return false;
      return this.modal.open === true || this.modal.hasAttribute('open');
    }

    onClick(e) {
      const openBtn = e.target.closest('[data-gal-compare-open]');
      if (openBtn) {
        e.preventDefault();
        this.hideLimitToast();
        this.openModal();
        return;
      }
      const closeBtn = e.target.closest('[data-gal-compare-close]');
      if (closeBtn) {
        e.preventDefault();
        this.closeModal();
        return;
      }
      const dismissLimit = e.target.closest('[data-gal-compare-limit-dismiss]');
      if (dismissLimit) {
        e.preventDefault();
        this.hideLimitToast();
        return;
      }
      const clearBtn = e.target.closest('[data-gal-compare-clear]');
      if (clearBtn) {
        e.preventDefault();
        this.hideLimitToast();
        writeCompareList([]);
        if (this.isModalOpen() || document.querySelector('gal-compare-board')) this.renderBoard();
        return;
      }
      const removeBtn = e.target.closest('[data-gal-compare-remove]');
      if (removeBtn) {
        e.preventDefault();
        const handle = removeBtn.dataset.productHandle;
        this.hideLimitToast();
        writeCompareList(readCompareList().filter((x) => x.handle !== handle));
        if (this.isModalOpen() || document.querySelector('gal-compare-board')) this.renderBoard();
        return;
      }
      const atcBtn = e.target.closest('[data-gal-compare-atc]');
      if (atcBtn) {
        e.preventDefault();
        this.addVariantToCart(atcBtn);
        return;
      }
      const btn = e.target.closest('[data-gal-compare]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      let list = readCompareList();
      const exists = list.some((x) => x.handle === handle);
      if (exists) {
        list = list.filter((x) => x.handle !== handle);
        this.hideLimitToast();
        writeCompareList(list);
        if (this.isModalOpen()) this.renderBoard();
        return;
      }
      if (list.length >= COMPARE_MAX) {
        this.showLimitToast();
        return;
      }
      list.push(metaFromButton(btn));
      this.hideLimitToast();
      writeCompareList(list);
    }

    onChange(e) {
      const select = e.target.closest('[data-gal-compare-variant]');
      if (!select) return;
      const handle = select.dataset.productHandle;
      const entry = this._boardEntries?.find((x) => x.product.handle === handle);
      if (!entry) return;
      entry.selectedVariantId = select.value;
      this.refreshVariantCells(handle);
    }

    showLimitToast() {
      if (!this.limitToast) {
        this.limitToast = document.querySelector('[data-gal-compare-limit]');
      }
      if (this.limitToast) {
        this.limitToast.hidden = false;
        this.limitToast.classList.add('is-visible');
        const msg = this.limitToast.querySelector('[data-gal-compare-limit-msg]');
        if (msg) msg.textContent = COMPARE_LIMIT_MSG;
        return;
      }
      if (this.status) this.status.textContent = COMPARE_LIMIT_MSG;
    }

    hideLimitToast() {
      if (!this.limitToast) this.limitToast = document.querySelector('[data-gal-compare-limit]');
      if (!this.limitToast) return;
      this.limitToast.hidden = true;
      this.limitToast.classList.remove('is-visible');
    }

    syncUi() {
      const list = readCompareList();
      const count = list.length;
      document.querySelectorAll('[data-gal-compare-count]').forEach((el) => {
        el.textContent = String(count);
      });
      document.querySelectorAll('[data-gal-compare-cta-label]').forEach((el) => {
        el.textContent = count > 0 ? `Comparer ${count}` : 'Comparer';
      });
      document.querySelectorAll('[data-gal-compare]').forEach((btn) => {
        const on = list.some((x) => x.handle === btn.dataset.productHandle);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = btn.querySelector('[data-gal-compare-label]');
        if (label) label.textContent = on ? COMPARE_ADDED : COMPARE_LABEL;
        else if (!btn.querySelector('img') && btn.childNodes.length <= 1) btn.textContent = on ? COMPARE_ADDED : COMPARE_LABEL;
      });
      if (this.dock) {
        const showTray = this.dock.dataset.compareTray !== '0';
        this.dock.hidden = !showTray || count < 1;
        this.dock.classList.toggle('is-visible', showTray && count >= 1);
        document.body.classList.toggle('gal-compare-tray-visible', showTray && count >= 1);
      }
      if (this.thumbs) {
        this.thumbs.innerHTML = list
          .map((item) => {
            const img = item.image
              ? `<img src="${escapeHtml(item.image)}" alt="" width="40" height="40" loading="lazy">`
              : `<span class="gal-compare-dock__thumb-fallback">${escapeHtml((item.title || '?').slice(0, 1))}</span>`;
            return `<button type="button" class="gal-compare-dock__thumb" data-gal-compare-remove data-product-handle="${escapeHtml(item.handle)}" title="Retirer ${escapeHtml(item.title || '')}" aria-label="Retirer ${escapeHtml(item.title || item.handle)}">${img}</button>`;
          })
          .join('');
      }
    }

    openModal() {
      if (!this.modal) {
        window.location.href = '/pages/compare';
        return;
      }
      if (typeof this.modal.showModal === 'function') {
        if (!this.isModalOpen()) this.modal.showModal();
      } else {
        this.modal.setAttribute('open', '');
      }
      document.body.classList.add('gal-compare-open');
      this.renderBoard();
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.isModalOpen()) this.modal.close();
      else this.modal.removeAttribute('open');
      document.body.classList.remove('gal-compare-open');
    }

    async addVariantToCart(btn) {
      const id = btn.dataset.variantId || btn.closest('[data-gal-compare-actions]')?.querySelector('[data-gal-compare-variant]')?.value;
      if (!id || !window.Galactica?.routes?.cart_add_url) return;
      btn.disabled = true;
      try {
        await fetch(window.Galactica.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
        document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
        document.dispatchEvent(new CustomEvent('gal:cart:open'));
        this.closeModal();
      } catch {
        window.location.href = `/cart/add?id=${encodeURIComponent(id)}&quantity=1`;
      } finally {
        btn.disabled = false;
      }
    }

    refreshVariantCells(handle) {
      const entry = this._boardEntries?.find((x) => x.product.handle === handle);
      if (!entry || !this.board) return;
      const col = this.board.querySelector(`[data-gal-compare-col="${CSS.escape(handle)}"]`);
      const root = this.board;
      const variant = pickVariant(entry.product, entry.selectedVariantId);
      root.querySelectorAll(`[data-gal-compare-price-cell="${CSS.escape(handle)}"]`).forEach((el) => {
        el.innerHTML = variantPriceHtml(variant, entry.product) || '—';
      });
      root.querySelectorAll(`[data-gal-compare-ppg-cell="${CSS.escape(handle)}"]`).forEach((el) => {
        el.textContent = variantPerGram(variant) || '—';
      });
      root.querySelectorAll(`[data-gal-compare-avail-cell="${CSS.escape(handle)}"]`).forEach((el) => {
        el.textContent = variantAvailability(variant, entry.product);
      });
      root.querySelectorAll(`[data-gal-compare-atc][data-product-handle="${CSS.escape(handle)}"]`).forEach((btn) => {
        const available = variant ? !!variant.available : !!entry.product.available;
        btn.dataset.variantId = variant ? String(variant.id) : '';
        btn.disabled = !available || !variant;
        btn.textContent = available ? 'Ajouter' : 'Épuisé';
      });
      void col;
    }

    setStatus(list) {
      if (!this.status) return;
      if (list.length === 0) {
        this.status.textContent = 'Ajoutez 2 ou 3 produits via le bouton Comparer.';
      } else if (list.length === 1) {
        this.status.textContent = 'Ajoutez encore 1 produit pour lancer la comparaison.';
      } else {
        this.status.textContent = `${list.length} produits sélectionnés.`;
      }
    }

    async renderBoard() {
      if (!this.board) return;
      const list = readCompareList();
      const renderToken = (this._renderToken = (this._renderToken || 0) + 1);
      if (this.loading) this.loading.hidden = false;
      this.board.innerHTML = '';
      this.setStatus(list);

      if (!list.length) {
        if (this.loading) this.loading.hidden = true;
        this.board.innerHTML = `<div class="gal-compare-empty"><p>Aucun produit pour l’instant.</p><button type="button" class="gal-btn gal-btn--secondary" data-gal-compare-close>Continuer mes achats</button></div>`;
        return;
      }

      const needleHost =
        this.loading?.querySelector('[data-gal-compare-loader]') ||
        this.loading?.querySelector('[data-gal-compare-needle]') ||
        this.loading;
      const play = window.Galactica?.playLoadingGauge || window.Galactica?.playNeedleMeter;
      const needlePromise =
        typeof play === 'function' && this.loading && !this.loading.hidden
          ? play(needleHost, { duration: 2000 })
          : Promise.resolve();

      const products = [];
      for (const item of list) {
        try {
          const res = await fetch(`/products/${item.handle}.js`);
          if (!res.ok) continue;
          const p = await res.json();
          const meta = persistCompareItem(item, p);
          const idx = list.findIndex((x) => x.handle === item.handle);
          if (idx >= 0) list[idx] = meta;
          const selectedVariantId = String(pickVariant(p)?.id || '');
          products.push({ product: p, meta, selectedVariantId });
        } catch {
          /* skip unavailable handles */
        }
      }

      if (renderToken !== this._renderToken) return;

      writeCompareList(list.map((x) => persistCompareItem(x)), { silent: true });
      this.syncUi();
      await needlePromise;
      if (renderToken !== this._renderToken) return;
      if (this.loading) this.loading.hidden = true;

      this._boardEntries = products;

      if (products.length < 2) {
        const only = products[0];
        this.board.innerHTML = `<div class="gal-compare-empty">
          ${
            only
              ? `<article class="gal-compare-card gal-compare-card--solo">
                  ${only.product.featured_image ? `<img src="${escapeHtml(only.product.featured_image)}" alt="" width="160" height="160" loading="lazy">` : ''}
                  <h3>${escapeHtml(only.product.title)}</h3>
                  <p class="gal-price">${money(only.product.price)}</p>
                  <button type="button" class="gal-btn gal-btn--text" data-gal-compare-remove data-product-handle="${escapeHtml(only.product.handle)}">Retirer</button>
                </article>`
              : ''
          }
          <p class="gal-muted">Ajoutez encore 1 produit pour lancer la comparaison.</p>
        </div>`;
        return;
      }

      const finderCtx = readFinderContext();
      const finderScores = products.map(({ product: p, meta }) => {
        if (!finderCtx) return 0;
        return scoreFinderProduct(
          {
            id: p.id,
            available: p.available,
            intents: meta.usageIntents,
            intensity: meta.intensity,
            sellingType: meta.sellingType,
            culture: meta.culture,
            type: p.type || meta.type,
            cbd: meta.cbd,
            title: p.title,
            tags: (p.tags || []).join(','),
          },
          finderCtx
        ).score;
      });

      const cbdValues = products.map(({ product: p, meta }) => cbdNumber(meta, p));
      const ppgValues = products.map(({ product: p, selectedVariantId }) => {
        const v = pickVariant(p, selectedVariantId);
        const label = v?.public_title || v?.title || v?.option1 || '';
        const g = parseGrams(label);
        if (!g || !(Number(v?.price) > 0)) return null;
        return v.price / g;
      });
      const cultureValues = products.map(({ meta }) => String(meta.culture || '').trim());
      const cultureKeys = cultureValues.map((c) => c.toLowerCase()).filter(Boolean);
      const cultureDiverse = new Set(cultureKeys).size > 1;
      const maxCbd = cbdValues.every((n) => n == null) ? null : Math.max(...cbdValues.filter((n) => n != null));
      const minPpg = ppgValues.every((n) => n == null) ? null : Math.min(...ppgValues.filter((n) => n != null));
      const cbdUniqueWinner =
        maxCbd != null && cbdValues.filter((n) => n != null && n === maxCbd).length === 1;
      const ppgUniqueWinner =
        minPpg != null && ppgValues.filter((n) => n != null && n === minPpg).length === 1;

      const highlightClass = (kind, idx) => {
        if (kind === 'cbd' && cbdUniqueWinner && cbdValues[idx] === maxCbd) return ' is-highlight is-highlight--intense';
        if (kind === 'ppg' && ppgUniqueWinner && ppgValues[idx] === minPpg) return ' is-highlight is-highlight--value';
        if (kind === 'culture' && cultureDiverse && cultureValues[idx]) {
          return ' is-highlight is-highlight--diff';
        }
        return '';
      };
      const highlightLabel = (kind, idx) => {
        if (kind === 'cbd' && cbdUniqueWinner && cbdValues[idx] === maxCbd) {
          return `<span class="gal-compare-chip">Plus intense</span>`;
        }
        if (kind === 'ppg' && ppgUniqueWinner && ppgValues[idx] === minPpg) {
          return `<span class="gal-compare-chip">Prix/g le plus bas</span>`;
        }
        return '';
      };

      const formatSelect = (p, selectedVariantId) => {
        const variants = p.variants || [];
        if (!variants.length) return '—';
        if (variants.length === 1) {
          return escapeHtml(variants[0].public_title || variants[0].title || 'Format unique');
        }
        return `<select class="gal-compare-variant" data-gal-compare-variant data-product-handle="${escapeHtml(p.handle)}" aria-label="Format ${escapeHtml(p.title)}">
          ${variants
            .map(
              (v) =>
                `<option value="${v.id}" ${String(v.id) === String(selectedVariantId) ? 'selected' : ''} ${v.available ? '' : 'disabled'}>${escapeHtml(v.public_title || v.title)}${v.available ? '' : ' — épuisé'}</option>`
            )
            .join('')}
        </select>`;
      };

      const rows = [
        {
          label: 'Image',
          hideIfEmpty: false,
          values: products.map(
            ({ product: p }) =>
              p.featured_image
                ? `<a class="gal-compare-board__media" href="/products/${escapeHtml(p.handle)}"><img src="${escapeHtml(p.featured_image)}" alt="" width="160" height="160" loading="lazy"></a>`
                : '—'
          ),
        },
        {
          label: 'Produit',
          hideIfEmpty: false,
          values: products.map(({ product: p }, idx) => {
            const badge =
              finderScores[idx] > 0
                ? `<span class="gal-compare-finder-badge">Correspond à votre recherche</span>`
                : '';
            return `<div class="gal-compare-board__product"><strong><a href="/products/${escapeHtml(p.handle)}">${escapeHtml(p.title)}</a></strong>${badge}</div>`;
          }),
        },
        {
          label: 'Type / Famille',
          values: products.map(({ product: p, meta }) => escapeHtml(formatFamily(p, meta))),
        },
        {
          label: 'CBD',
          values: products.map(({ product: p, meta }, idx) => {
            const gauge = vuCell('CBD', meta.cbd || moleculeFromProduct(p, 'cbd') || extractCbdLabel(p));
            return `<div class="gal-compare-board__metric${highlightClass('cbd', idx)}">${gauge}${highlightLabel('cbd', idx)}</div>`;
          }),
        },
        {
          label: 'CBG',
          values: products.map(({ product: p, meta }) => vuCell('CBG', meta.cbg || moleculeFromProduct(p, 'cbg'))),
        },
        {
          label: 'CBC',
          values: products.map(({ product: p, meta }) => vuCell('CBC', meta.cbc || moleculeFromProduct(p, 'cbc'))),
        },
        {
          label: 'Intensité',
          values: products.map(({ meta }) => escapeHtml(meta.intensity || '')),
        },
        {
          label: 'Usage',
          values: products.map(({ meta }) => escapeHtml(formatUsageIntents(meta.usageIntents))),
        },
        {
          label: 'Culture',
          values: products.map(
            ({ meta }, idx) =>
              `<div class="gal-compare-board__metric${highlightClass('culture', idx)}">${escapeHtml(meta.culture || '') || '—'}</div>`
          ),
        },
        {
          label: 'Origine',
          values: products.map(({ meta }) => escapeHtml(meta.origin || '')),
        },
        {
          label: 'Arômes',
          values: products.map(({ meta }) => escapeHtml(meta.flavor || '')),
        },
        {
          label: 'Texture',
          values: products.map(({ meta }) => escapeHtml(meta.texture || '')),
        },
        {
          label: 'Prix',
          hideIfEmpty: false,
          values: products.map(({ product: p, selectedVariantId }) => {
            const v = pickVariant(p, selectedVariantId);
            return `<div data-gal-compare-price-cell="${escapeHtml(p.handle)}">${variantPriceHtml(v, p)}</div>`;
          }),
        },
        {
          label: '€/g',
          values: products.map(({ product: p, selectedVariantId }, idx) => {
            const v = pickVariant(p, selectedVariantId);
            const ppg = variantPerGram(v);
            return `<div class="gal-compare-board__metric${highlightClass('ppg', idx)}" data-gal-compare-ppg-cell="${escapeHtml(p.handle)}">${ppg || '—'}${highlightLabel('ppg', idx)}</div>`;
          }),
        },
        {
          label: 'Formats',
          hideIfEmpty: false,
          values: products.map(({ product: p, selectedVariantId }) => formatSelect(p, selectedVariantId)),
        },
        {
          label: 'Disponibilité',
          hideIfEmpty: false,
          values: products.map(({ product: p, selectedVariantId }) => {
            const v = pickVariant(p, selectedVariantId);
            return `<span data-gal-compare-avail-cell="${escapeHtml(p.handle)}">${variantAvailability(v, p)}</span>`;
          }),
        },
        {
          label: 'Rating',
          values: products.map(({ meta }) => ratingHtml(meta)),
        },
        {
          label: 'Actions',
          hideIfEmpty: false,
          values: products.map(({ product: p, selectedVariantId }) => {
            const v = pickVariant(p, selectedVariantId);
            const available = v ? !!v.available : !!p.available;
            return `<div class="gal-compare-card__actions" data-gal-compare-actions data-gal-compare-col="${escapeHtml(p.handle)}">
              <button type="button" class="gal-btn gal-btn--primary" data-gal-compare-atc data-product-handle="${escapeHtml(p.handle)}" data-variant-id="${v ? v.id : ''}" ${available && v ? '' : 'disabled'}>${available ? 'Ajouter' : 'Épuisé'}</button>
              <a class="gal-btn gal-btn--secondary" href="/products/${escapeHtml(p.handle)}">Voir</a>
              <button type="button" class="gal-btn gal-btn--text" data-gal-compare-remove data-product-handle="${escapeHtml(p.handle)}">Retirer</button>
            </div>`;
          }),
        },
      ].filter((row) => {
        if (row.hideIfEmpty === false) return true;
        return row.values.some((v) => cellHasValue(v));
      });

      this.board.innerHTML = `<div class="gal-compare-board" style="--gal-compare-cols:${products.length}">
        <div class="gal-compare-board__scroller">
          <div class="gal-compare-board__head">
            <div class="gal-compare-board__label"></div>
            ${products
              .map(
                ({ product: p }) =>
                  `<div class="gal-compare-board__col-head"><button type="button" class="gal-btn gal-btn--text" data-gal-compare-remove data-product-handle="${escapeHtml(p.handle)}">Retirer</button></div>`
              )
              .join('')}
          </div>
          ${rows
            .map(
              (row) => `<div class="gal-compare-board__row"${row.label ? '' : ' data-gal-compare-actions-row'}>
                <div class="gal-compare-board__label">${escapeHtml(row.label)}</div>
                ${row.values.map((v) => `<div class="gal-compare-board__cell">${v || '—'}</div>`).join('')}
              </div>`
            )
            .join('')}
        </div>
      </div>`;
      if (typeof window.Galactica?.initGauges === 'function') {
        window.Galactica.initGauges(this.board);
      } else {
        document.dispatchEvent(new CustomEvent('gal:gauges:refresh', { detail: { root: this.board } }));
      }
    }
  }

  class GalCompareBoard extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galCompareBoardBound === '1') return;
      this.dataset.galCompareBoardBound = '1';
      this.board = this.querySelector('[data-gal-compare-board]');
      const section = this.closest('section') || this.parentElement;
      this.statusEl =
        section?.querySelector?.('[data-gal-compare-status]') ||
        document.querySelector('section .gal-compare-page [data-gal-compare-status], .gal-compare-page [data-gal-compare-status], [data-gal-compare-status]');

      const syncPage = async () => {
        if (!this.board) return;
        const modal = document.querySelector('dialog[data-gal-compare-modal]');
        if (modal?.open) return;
        const host = document.querySelector('gal-compare');
        if (!host || typeof host.renderBoard !== 'function') return;
        const prevBoard = host.board;
        const prevStatus = host.status;
        const prevLoading = host.loading;
        host.board = this.board;
        host.status = this.statusEl || host.status;
        host.loading = null;
        await host.renderBoard();
        host.board = prevBoard;
        host.status = prevStatus;
        host.loading = prevLoading;
      };

      document.addEventListener('gal:compare:update', () => {
        syncPage();
      });
      syncPage();
    }
  }

  class GalWishlistBoard extends HTMLElement {
    connectedCallback() {
      this.board = this.querySelector('[data-gal-wishlist-board]');
      this.empty = this.querySelector('[data-gal-wishlist-empty]');
      this.render();
      document.addEventListener('gal:wishlist:update', () => this.render());
    }

    read() {
      return readWishlist();
    }

    async render() {
      const handles = this.read();
      if (!handles.length) {
        if (this.board) this.board.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        return;
      }
      if (this.empty) this.empty.hidden = true;
      if (!this.board) return;
      this.board.innerHTML = '<p class="gal-muted">Chargement…</p>';
      const cards = [];
      for (const handle of handles) {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) continue;
          const p = await res.json();
          const v = (p.variants || []).find((x) => x.available) || p.variants?.[0];
          const perGram = minPricePerGram(p);
          cards.push(`<article class="gal-box gal-box--bordered" style="padding:1rem">
            ${p.featured_image ? `<a href="/products/${p.handle}"><img src="${p.featured_image}" alt="" width="400" height="400" loading="lazy" style="width:100%;aspect-ratio:1;object-fit:cover"></a>` : ''}
            <h3><a href="/products/${p.handle}">${p.title}</a></h3>
            <p class="gal-price">${money(p.price)}${perGram ? ` <span class="gal-price__per-gram">${perGram}</span>` : ''}</p>
            <div style="display:flex;flex-wrap:wrap;gap:.5rem;margin-top:.75rem">
              <a class="gal-btn gal-btn--primary" href="/products/${p.handle}">Voir</a>
              ${v ? `<button type="button" class="gal-btn gal-btn--action" data-gal-wishlist-quick-add data-variant-id="${v.id}">Ajouter</button>` : ''}
              <button type="button" class="gal-btn gal-btn--text" data-gal-wishlist data-product-handle="${p.handle}" data-product-id="${p.id}">Retirer</button>
            </div>
          </article>`);
        } catch (e) {
          /* skip */
        }
      }
      this.board.innerHTML = cards.length ? cards.join('') : '<p class="gal-muted">Aucun produit valide dans ta liste.</p>';
      this.board.querySelectorAll('[data-gal-wishlist-quick-add]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.variantId;
          if (!id) return;
          try {
            await fetch(window.Galactica.routes.cart_add_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
            });
            document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
            document.dispatchEvent(new CustomEvent('gal:cart:open'));
          } catch (err) {
            /* ignore */
          }
        });
      });
    }
  }

  class GalCarousel extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-gal-carousel-track]');
      this.querySelector('[data-gal-carousel-prev]')?.addEventListener('click', () => {
        track?.scrollBy({ left: -320, behavior: 'smooth' });
      });
      this.querySelector('[data-gal-carousel-next]')?.addEventListener('click', () => {
        track?.scrollBy({ left: 320, behavior: 'smooth' });
      });
    }
  }

  class GalQuickView extends HTMLElement {
    connectedCallback() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-gal-quick-view]');
        if (!btn) return;
        const handle = btn.dataset.productHandle;
        if (!handle) return;
        this.innerHTML = `<div class="gal-drawer is-open" role="dialog" aria-modal="true"><div class="gal-drawer__overlay" data-close></div><div class="gal-drawer__panel"><button type="button" class="gal-btn gal-btn--text" data-close>Fermer</button><p>Chargement…</p></div></div>`;
        const close = () => {
          this.innerHTML = '';
        };
        this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));

        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) throw new Error('fetch failed');
          const p = await res.json();
          const v = (p.variants || []).find((x) => x.available) || p.variants?.[0];
          const label = v?.public_title || v?.title || v?.option1 || '';
          const grams = parseGrams(label);
          const perGram = grams && v ? `${money(Math.round(v.price / grams))}/g` : minPricePerGram(p);
          const panel = this.querySelector('.gal-drawer__panel');
          if (panel) {
            panel.innerHTML = `
              <button type="button" class="gal-btn gal-btn--text" data-close>Fermer</button>
              ${p.featured_image ? `<img src="${p.featured_image}" alt="" width="600" height="600" loading="lazy" style="width:100%;max-height:320px;object-fit:cover">` : ''}
              <h2 style="margin-top:1rem">${p.title}</h2>
              <p class="gal-price">${money(v?.price || p.price)}${perGram ? ` <span class="gal-price__per-gram">${perGram}</span>` : ''}</p>
              <div style="display:flex;flex-wrap:wrap;gap:.5rem;margin-top:1rem">
                <a class="gal-btn gal-btn--primary" href="/products/${p.handle}">Voir la fiche</a>
                ${
                  v
                    ? `<button type="button" class="gal-btn gal-btn--action" data-gal-qv-add data-variant-id="${v.id}" ${v.available ? '' : 'disabled'}>Ajouter</button>`
                    : ''
                }
              </div>`;
            panel.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));
            panel.querySelector('[data-gal-qv-add]')?.addEventListener('click', async () => {
              const id = v?.id;
              if (!id) return;
              try {
                await fetch(window.Galactica.routes.cart_add_url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
                });
                document.dispatchEvent(new CustomEvent('gal:cart:refresh'));
                document.dispatchEvent(new CustomEvent('gal:cart:open'));
                close();
              } catch (err) {
                window.location.href = `/products/${p.handle}`;
              }
            });
          }
        } catch (err) {
          const panel = this.querySelector('.gal-drawer__panel');
          if (panel) {
            panel.innerHTML = `<button type="button" class="gal-btn gal-btn--text" data-close>Fermer</button><p>Impossible de charger le produit.</p><a class="gal-btn gal-btn--primary" href="/products/${handle}">Voir la fiche</a>`;
            panel.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', close));
          }
        }
      });
    }
  }

  class GalVariantSelector extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', () => {
        this.dispatchEvent(new CustomEvent('gal:variant-change', { bubbles: true }));
      });
    }
  }

  class GalRelatedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galRelatedReady === '1') return;
      this.dataset.galRelatedReady = '1';
      this.limit = Number(this.dataset.limit || 4) || 4;
      this.currentProductId = this.dataset.productId || '';
      this.contextualEnabled = this.dataset.contextual === '1';
      this.fallbackHtml = this.querySelector('[data-gal-related-grid]')?.innerHTML || '';
      this.onContext = () => this.applyContextualOrFallback();
      document.addEventListener('gal:finder:context', this.onContext);
      this.applyContextualOrFallback();
      const url = this.dataset.url;
      if (url && !readFinderContext()) this.fetchRelated(url);
      this.refreshGauges();
    }

    refreshGauges() {
      const grid = this.querySelector('[data-gal-related-grid]') || this;
      /* Reset play flags on freshly inserted cards so viewport anim can run */
      grid.querySelectorAll('[data-gal-gauge-stack], [data-gal-gauges]').forEach((el) => {
        delete el.dataset.galGaugePlayed;
      });
      grid.querySelectorAll('.gal-gauge[data-gal-gauge-pct]').forEach((el) => {
        delete el.dataset.galPlayed;
        delete el.dataset.galGaugePlayed;
        el.classList.remove('is-played', 'is-playing');
        const fill = el.querySelector('.gal-gauge__fill');
        if (fill) {
          fill.style.transition = 'none';
          fill.style.width = '0%';
        }
        const valueEl = el.querySelector('.gal-gauge__value');
        if (valueEl) valueEl.textContent = '0%';
      });
      if (typeof window.Galactica?.initGauges === 'function') {
        window.Galactica.initGauges(grid);
      } else {
        document.dispatchEvent(new CustomEvent('gal:gauges:refresh', { detail: { root: grid } }));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('gal:finder:context', this.onContext);
    }

    applyContextualOrFallback() {
      const titleEl = this.querySelector('[data-gal-related-title]');
      const subEl = this.querySelector('[data-gal-related-subtitle]');
      const grid = this.querySelector('[data-gal-related-grid]');
      if (!grid) return;

      const ctx = this.contextualEnabled ? readFinderContext() : null;
      if (!ctx) {
        this.classList.remove('is-contextual');
        if (titleEl) titleEl.textContent = this.dataset.titleDefault || titleEl.textContent;
        if (subEl) {
          subEl.hidden = true;
          subEl.textContent = '';
        }
        if (this.fallbackHtml && !grid.querySelector('.gal-card')) {
          grid.innerHTML = this.fallbackHtml;
        }
        this.refreshGauges();
        return;
      }

      const poolItems = [...this.querySelectorAll('[data-gal-related-item]')];
      if (!poolItems.length) {
        this.classList.remove('is-contextual');
        return;
      }

      const ranked = poolItems
        .map((el) => {
          const meta = {
            id: el.dataset.id,
            available: el.dataset.available,
            intents: el.dataset.intents,
            intensity: el.dataset.intensity,
            culture: el.dataset.culture,
            sellingType: el.dataset.sellingType,
            type: el.dataset.type,
            tags: el.dataset.tags,
            collections: el.dataset.collections,
            cbd: el.dataset.cbd,
            title: el.dataset.title,
          };
          return { el, ...scoreFinderProduct(meta, ctx, { excludeId: this.currentProductId }) };
        })
        .filter((row) => row.score > 0)
        .sort((a, b) => a.tier - b.tier || b.score - a.score);

      const familyWanted = ctx.family && ctx.family !== 'any';
      const formatFirst = familyWanted ? ranked.filter((r) => r.formatHit) : [];
      const rankedPool =
        familyWanted && formatFirst.length >= Math.min(2, this.limit) ? formatFirst.concat(ranked.filter((r) => !r.formatHit)) : ranked;

      const pick = [];
      const take = (predicate, needed) => {
        for (const row of rankedPool) {
          if (pick.length >= needed) break;
          if (pick.includes(row)) continue;
          if (predicate(row)) pick.push(row);
        }
      };

      if (familyWanted) take((r) => r.formatHit && r.tier === 1, this.limit);
      if (familyWanted) take((r) => r.formatHit && r.tier <= 2, this.limit);
      if (familyWanted) take((r) => r.formatHit, this.limit);
      take((r) => r.tier === 1, this.limit);
      take((r) => r.tier <= 2, this.limit);
      take((r) => r.tier <= 3, this.limit);
      take((r) => r.tier <= 4, this.limit);
      take(() => true, this.limit);

      if (!pick.length) {
        this.classList.remove('is-contextual');
        if (titleEl) titleEl.textContent = this.dataset.titleDefault || 'Vous pourriez aussi aimer';
        if (subEl) subEl.hidden = true;
        if (this.fallbackHtml) grid.innerHTML = this.fallbackHtml;
        this.refreshGauges();
        return;
      }

      this.classList.add('is-contextual');
      if (titleEl) titleEl.textContent = this.dataset.titleContextual || 'Sélection selon vos préférences';
      if (subEl) {
        if (this.dataset.showSubtitle === '1') {
          subEl.textContent = this.dataset.subtitleContextual || 'Des produits proches de votre sélection';
          subEl.hidden = false;
        } else {
          subEl.hidden = true;
        }
      }
      grid.innerHTML = pick.map((row) => row.el.innerHTML).join('');
      this.refreshGauges();
    }

    async fetchRelated(url) {
      if (readFinderContext()) return;
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('gal-related-products') || doc.querySelector('.gal-related');
        const remoteGrid = incoming?.querySelector('[data-gal-related-grid]');
        const local = this.querySelector('[data-gal-related-grid]');
        if (!remoteGrid || !local || !remoteGrid.innerHTML.trim()) return;
        local.innerHTML = remoteGrid.innerHTML;
        this.fallbackHtml = remoteGrid.innerHTML;
        // Keep pool from current document; re-apply if context arrives later
        this.applyContextualOrFallback();
        this.refreshGauges();
      } catch (_) {
        /* keep fallback */
      }
    }
  }

  class GalHomeShowcase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galHsReady === '1') return;
      this.dataset.galHsReady = '1';
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.querySelectorAll('.gal-home-showcase__track').forEach((track) => {
          track.style.animation = 'none';
          track.style.transform = 'none';
        });
      }
    }
  }

  const registry = {
    'gal-product-card': GalProductCard,
    'gal-cart-drawer': GalCartDrawer,
    'gal-predictive-search': GalPredictiveSearch,
    'gal-quiz': GalQuiz,
    'gal-order-tracker': GalOrderTracker,
    'gal-compare': GalCompare,
    'gal-compare-board': GalCompareBoard,
    'gal-wishlist-board': GalWishlistBoard,
    'gal-carousel': GalCarousel,
    'gal-quick-view': GalQuickView,
    'gal-variant-selector': GalVariantSelector,
    'gal-related-products': GalRelatedProducts,
    'gal-home-showcase': GalHomeShowcase,
  };

  Object.entries(registry).forEach(([name, ctor]) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  });

  document.addEventListener('click', (e) => {
    const wish = e.target.closest('[data-gal-wishlist]');
    if (!wish) return;
    const handle = wish.dataset.productHandle;
    if (!handle) return;
    const key = 'gal-wishlist';
    let list = readWishlist();
    if (list.includes(handle)) list = list.filter((x) => x !== handle);
    else list.push(handle);
    localStorage.setItem(key, JSON.stringify(list));
    wish.setAttribute('aria-pressed', list.includes(handle) ? 'true' : 'false');
    document.dispatchEvent(new CustomEvent('gal:wishlist:update', { detail: { list } }));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWishlistPressed);
  } else {
    syncWishlistPressed();
  }
})();
